import type { KeycloakInstance } from 'keycloak-js';
import { forEach, isArray, mapValues, zipObject } from 'lodash-es';
import { log } from '../log';

const eventMethodMapping = {
  ready: 'onReady',
  authentication: 'onAuthSuccess',
  authenticationFailed: 'onAuthError',
  tokenRefresh: 'onAuthRefreshSuccess',
  tokenRefreshFailed: 'onAuthRefreshError',
  tokenExpired: 'onTokenExpired',
  logout: 'onAuthLogout',
  actionStatus: 'onActionUpdate',
} as const;

type SessionEvent = keyof typeof eventMethodMapping;

const sessionEvents = Object.keys(eventMethodMapping) as SessionEvent[];

type SessionEventListener = () => void | (() => Promise<void>);
type SessionEventListeners = Record<SessionEvent, SessionEventListener[]>;

export class SessionEvents {

  private eventListeners = zipObject(
    sessionEvents,
    sessionEvents.map(() => [] as SessionEventListener[]),
  ) as SessionEventListeners;

  constructor(
    private keycloakInstance: KeycloakInstance,
  ) {
    this.setupEventListeners();
  }

  on(event: SessionEvent | SessionEvent[], listener: SessionEventListener) {
    const eventNames = isArray(event) ? event : [event];

    const removeListeners = eventNames.map(
      eventName => this.addEventListener(eventName, listener),
    );

    return () => removeListeners.forEach(removeListener => removeListener());
  }

  destroy() {
    this.removeAllEventListeners();
  }

  private removeAllEventListeners() {
    this.eventListeners = mapValues(
      this.eventListeners,
      () => [],
    );
  }

  private addEventListener(eventName: SessionEvent, listener: SessionEventListener) {
    this.eventListeners[eventName] = [
      ...this.eventListeners[eventName],
      listener,
    ];

    return () => {
      this.eventListeners[eventName].filter(
        eventListener => eventListener !== listener,
      );
    };
  }

  private setupEventListeners() {
    forEach(
      eventMethodMapping,
      (method, eventName) => this.keycloakInstance[method] = async (...args: unknown[]) => {
        log.debug(`Keycloak event ${eventName} fired`, args);

        for (const eventListener of this.eventListeners[eventName as SessionEvent]) {
          await eventListener();
        }
      },
    );
  }
}
